import { ReactComponent as CalendarFilled } from 'assets/icons/CalendarFilled.svg';
import { ReactComponent as FilePdfFilled } from 'assets/icons/FilePdfFilled.svg';
import { ReactComponent as Badge } from 'assets/icons/Badge.svg';
import {
  NotificationItem,
  NotificationType,
} from '_business/types/Notifications';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';
import { App, Button, Spin, Alert } from 'antd';
import clsx from 'clsx';
import formatTime from '_technical/utils/date';
import { useMarkNotificationAsRead } from '_technical/layouts/notifications_hook';
import { useHistory } from 'react-router';
import {
  requestUserNotificationToken,
  shouldShowNotificationOptIn,
} from '_technical/firebase/notifications';

function NotificationIcon({
  notification,
}: {
  notification: NotificationItem;
}) {
  switch (notification.type) {
    case NotificationType.DEPOSER_DEVIS:
    case NotificationType.DEPOTS_PIECES_ADMINISTRATIVES:
    case NotificationType.DEVIS_NON_CONFORME:
      return <FilePdfFilled />;
    default:
      return <CalendarFilled />;
  }
}

interface NotificationsPopoverProps {
  notifications: NotificationItem[];
  onClose: () => void;
  loading: boolean;
}

export default function NotificationsList({
  onClose,
  notifications,
  loading,
}: NotificationsPopoverProps) {
  const { t } = useTranslation('notifications');
  const { modal } = App.useApp();
  const history = useHistory();
  const { mutate } = useMarkNotificationAsRead();
  const [showOptInAlert, setShowOptInAlert] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    const shouldShow = shouldShowNotificationOptIn();
    setShowOptInAlert(shouldShow);

    if (
      shouldShow &&
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      setIsRequesting(true);
      requestUserNotificationToken({ showSettingsAlert: false })
        .then((result) => {
          if (result.status === 'success') {
            setShowOptInAlert(false);
          }
        })
        .finally(() => {
          setIsRequesting(false);
        });
    }
  }, []);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    const result = await requestUserNotificationToken({
      showSettingsAlert: true,
    });
    if (result.status === 'success') {
      setShowOptInAlert(false);
    } else if (result.shouldShowSettingsAlert) {
      const content =
        result.status === 'browser-blocked'
          ? t('enable_settings_google')
          : t('enable_settings');
      modal.warning({
        title: t('title'),
        content,
        okText: 'OK',
      });
    }
    setIsRequesting(false);
  };

  return (
    <div
      className={
        'bg-white rounded px-1 flex flex-col gap-4 max-w-[90vw] lg:w-[559px] w-[320px]'
      }
    >
      <h4 className={'text-xl font-medium flex items-center justify-between'}>
        <span>{t('title')}</span>
        <Button
          onClick={onClose}
          variant={'text'}
          shape="circle"
          icon={<CloseOutlined />}
        />
      </h4>
      {showOptInAlert && (
        <Alert
          type="info"
          showIcon
          message={t('enable_info')}
          action={
            <Button
              size="small"
              type="primary"
              loading={isRequesting}
              onClick={handleEnableNotifications}
            >
              {t('enable_action')}
            </Button>
          }
        />
      )}
      {!loading && notifications.length === 0 && <p>{t('empty')}</p>}
      {loading && (
        <div className={'flex justify-center'}>
          <Spin />
        </div>
      )}
      <div className={'max-h-[60vh] overflow-y-auto'}>
        {notifications
          .sort((a, b) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          })
          .map((notification) => (
            <div
              onClick={() => {
                onClose();
                mutate(notification.id);
                history.push(
                  `/projets/${notification.transactionId}/informations`,
                );
              }}
              key={notification.id}
              className={clsx(
                'border-b border-solid border-divider p-3 cursor-pointer lg:active:bg-white',
                {
                  'bg-white lg:hover:bg-BleuHellio-0.5': notification.read,
                  'bg-BleuHellio-1 lg:hover:bg-BleuHellio-2':
                    !notification.read,
                },
              )}
            >
              <div className={'flex items-end gap-2 relative'}>
                {!notification.read && (
                  <div className={'absolute top-0 right-0'}>
                    <Badge />
                  </div>
                )}
                <div className={'flex grow items-start gap-2'}>
                  <div className="p-2 rounded-md bg-BleuHellio-1">
                    <NotificationIcon notification={notification} />
                  </div>
                  <div className={'flex flex-col gap-3.5'}>
                    <p
                      className={clsx(
                        '!leading-none text-grayTransparent text-sm',
                        {
                          'font-semibold': !notification.read,
                          'font-normal': notification.read,
                        },
                      )}
                    >
                      {t(notification.title)}
                    </p>
                    <p
                      className={
                        'text-sm text-graySemiTransparent !leading-none'
                      }
                    >
                      {notification.description}
                    </p>
                  </div>
                </div>
                <p
                  className={
                    'text-[11px] text-Industriels !leading-none text-nowrap'
                  }
                >
                  {formatTime(notification.date)}
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
